import { useMutation, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

//App
import { queryKeys } from "../constants";
import { employeePledgeApi, organizationApi } from "../../../../app/model/api";
import { PledgeSaveDto } from "../../../../app/model/api/types/dto";
import { PaymentOption } from "../../../../types";
import { encrypt } from "../../../../utility/helpers";
import { apiErrorMessage } from "../../../../utility/lib/error";

export const useEmployeePledgeLookupsQuery = () =>
  useQuery({
    queryKey: [queryKeys.getEmployeePledgeLookups],
    queryFn: () => employeePledgeApi.getLookups(),
  });

export const useEmployeePledgeQuery = (campaignId: string | undefined) =>
  useQuery({
    queryKey: [queryKeys.getEmployeePledge, campaignId],
    queryFn: () => employeePledgeApi.get(campaignId ?? ""),
    enabled: !!campaignId,
  });

export const useCampaignQuery = (campaignId: string | undefined) =>
  useQuery({
    queryKey: [queryKeys.getCampaign, campaignId],
    queryFn: () => organizationApi.getOrganizationCampaign(campaignId ?? ""),
    enabled: !!campaignId,
  });

export const useContributorQuery = (contributorId: string) =>
  useQuery({
    queryKey: [queryKeys.getContributor, contributorId],
    queryFn: () => employeePledgeApi.getContributor(contributorId),
    enabled: !!contributorId,
  });

export const useSaveEmployeePledge = () => {
  const navigate = useNavigate();
  const pledgeLookupsQuery = useEmployeePledgeLookupsQuery();

  return useMutation<void, Error, PledgeSaveDto>({
    mutationFn: async (payload: PledgeSaveDto) => {
      if (
        payload &&
        payload.paymentOptionId === PaymentOption.CreditCard &&
        payload.creditCardInformation
      ) {
        const encryptionKey =
          pledgeLookupsQuery.data?.result.encryptionKey ?? "";
        const creditCardNumber =
          payload.creditCardInformation?.creditCardNumber;

        payload = {
          ...payload,
          creditCardInformation: {
            ...payload.creditCardInformation,
            creditCardNumber: encrypt(creditCardNumber, encryptionKey),
          },
        };
      }
      const response = await employeePledgeApi.save(payload);
      if (response.isSuccess) {
        navigate(`/pledge-success/${payload.campaignId}`);
      } else {
        const message = apiErrorMessage(response.errorMessages) || "Failed to submit pledge.";
        throw new Error(message);
      }
    },
  });
};
